import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { cacheAgenda, readAgendaCache } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Navigation, MapPin, Clock, Route as RouteIcon, ChevronRight } from "lucide-react";

export default function AgendaTecnico() {
  const { user } = useAuth();
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState("");
  const [agenda, setAgenda] = useState(null);

  useEffect(() => {
    if (user?.role === "azienda") {
      api.get("/users", { params: { role: "tecnico" } }).then(({ data }) => {
        setTechs(data);
        if (data[0] && !techId) setTechId(data[0].id);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.role === "azienda" && !techId) return;
    api
      .get("/technician/agenda", { params: { day, ...(techId ? { technician_id: techId } : {}) } })
      .then(({ data }) => {
        setAgenda(data);
        cacheAgenda(day, data);
      })
      .catch(() => {
        const cached = readAgendaCache(day);
        if (cached) setAgenda({ ...cached, offline: true });
      });
  }, [day, techId, user]);

  return (
    <div data-testid="agenda-page">
      <PageHeader title="Agenda giornaliera" subtitle={agenda?.technician?.name || "Percorso ottimizzato"}>
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
               className="h-11 rounded-sm w-full sm:w-44" data-testid="agenda-date-input" />
        {user?.role === "azienda" && (
          <Select value={techId} onValueChange={setTechId}>
            <SelectTrigger className="h-11 rounded-sm w-full sm:w-52" data-testid="agenda-tech-select">
              <SelectValue placeholder="Tecnico" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {techs.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Fermate</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2" data-testid="agenda-stops-count">
            {agenda?.stops?.length ?? 0}
          </p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Km stimati</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2" data-testid="agenda-total-km">
            {agenda?.total_km ?? 0}
          </p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Guida stimata</p>
          <p className="font-display text-4xl font-black tracking-tighter mt-2">
            {agenda?.estimated_drive_min ?? 0}′
          </p>
        </Card>
      </div>

      {agenda?.maps_url && (
        <Button asChild className="rounded-sm h-12 mb-8 w-full sm:w-auto">
          <a href={agenda.maps_url} target="_blank" rel="noreferrer" data-testid="open-route-btn">
            <RouteIcon className="h-4 w-4 mr-2" /> Apri percorso ottimale su Google Maps
          </a>
        </Button>
      )}

      <div className="relative pl-6 space-y-4" data-testid="agenda-timeline">
        <span className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
        {(agenda?.stops || []).map((s) => (
          <div key={s.id} className="relative stagger-in">
            <span className="absolute -left-6 top-5 h-4 w-4 rounded-full bg-primary border-2 border-background" />
            <Card className="p-5 rounded-sm shadow-none" data-testid={`agenda-stop-${s.order}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs bg-primary text-primary-foreground px-2 py-0.5">
                      {s.order}
                    </span>
                    <p className="font-semibold">{s.customer_name}</p>
                    <Badge variant="outline" className="rounded-sm capitalize">{s.type}</Badge>
                    <Badge variant="secondary" className="rounded-sm capitalize">{s.status.replace("_", " ")}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {s.scheduled_time || "—"} · {s.plant_name || "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {s.address || "—"}, {s.city || "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono">
                    +{s.distance_from_prev_km} km · ~{s.eta_min}′ · {eur(s.totals?.total)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button asChild size="sm" className="rounded-sm h-11 flex-1">
                  <Link to={`/interventi/${s.id}`} data-testid={`agenda-open-${s.order}`}>
                    Gestisci <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
                {s.lat && (
                  <Button asChild variant="outline" size="sm" className="rounded-sm h-11">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`agenda-nav-${s.order}`}
                    >
                      <Navigation className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            </Card>
          </div>
        ))}
        {agenda && agenda.stops.length === 0 && (
          <Card className="p-6 rounded-sm shadow-none">
            <p className="text-sm text-muted-foreground">Nessun intervento pianificato per questa data.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
